import { useState } from "react";

import CampaignTypeIcon from "common/components/CampaignTypeIcon";
import {
  MobileSelectionListItem,
  PaginatedMobileSelectionList,
} from "common/components/MobileSelectionList";
import { CAMPAIGN_TYPES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { Campaign } from "common/types";

import {
  CAMPAIGN_OUTREACH_OPTIONS,
  CAMPAIGNS_COLUMNS,
} from "../../../constants";
import CampaignPreview from "./CampaignPreview";

interface MobileListProps {
  campaigns: Campaign[];
  selectedCampaigns: Campaign[];
  setSelectedCampaigns: (selectedCampaigns: Campaign[]) => void;
}

export default function MobileList({
  campaigns,
  selectedCampaigns,
  setSelectedCampaigns,
}: MobileListProps) {
  const { permissions } = useSelectedWorkspaceContext();
  const hasCampaignsFullPermission = permissions.includes("campaigns_full");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>(null);

  return (
    <>
      <PaginatedMobileSelectionList
        title="Campaigns"
        data={campaigns}
        localStorageId="campaigns"
        selection={selectedCampaigns}
        onSelectionChange={
          hasCampaignsFullPermission
            ? (newSelection) => {
                setSelectedCampaigns(
                  newSelection === "all" ? campaigns : newSelection,
                );
              }
            : undefined
        }
        allItems={CAMPAIGNS_COLUMNS}
      >
        {(campaign, listItemProps) => {
          const { type, name, outreach_type: outreachType } = campaign;

          const { icon: SubtypeIcon } = CAMPAIGN_OUTREACH_OPTIONS[outreachType];
          const { label } = CAMPAIGN_TYPES[type];

          return (
            <MobileSelectionListItem
              onClick={() => {
                setSelectedCampaign(campaign);
                setIsPreviewOpen(true);
              }}
              {...listItemProps}
            >
              <CampaignTypeIcon
                type={type}
                variant={outreachType === "drip" ? "drip" : "default"}
                size="sm"
              />

              <div className="flex flex-col items-start">
                <h3 className="text-xs-bold text-left line-clamp-1">{name}</h3>

                <div className="flex items-center gap-x-1 text-black-500">
                  <SubtypeIcon size={16} />
                  <span className="line-clamp-1 break-all text-button-xs">
                    {label} • {outreachType === "drip" ? "1st" : "2nd & 3rd+"}{" "}
                    connections
                  </span>
                </div>
              </div>
            </MobileSelectionListItem>
          );
        }}
      </PaginatedMobileSelectionList>

      {selectedCampaign && (
        <CampaignPreview
          isOpen={isPreviewOpen}
          setIsOpen={setIsPreviewOpen}
          campaign={selectedCampaign}
        />
      )}
    </>
  );
}
